import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// react-router components
import { Routes, Route, BrowserRouter } from "react-router-dom";
import theme from "assets/theme";
import { useSoftUIController, setDeviceLayout } from "hooks/context";

// components
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftComponents/SoftBox";

// mapbox and routes
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import routes from "page_routes";
import userAPI from "services/api/user";
import { useQuery } from '@tanstack/react-query';

import PageLayout from "layouts/PageLayout";
import BaseNavbar from "layouts/BaseNavbar";

import { setMapIcons } from "utils/map";
import moment from "moment";

const getActiveMode = (userMode) => {
    if (userMode !== "system") return userMode;
    const hour = moment().hour();
    return (hour >= 18 || hour < 6) ? "dark" : "light";
};
import SoftTypography from "components/SoftComponents/SoftTypography";
import MapView from "pages/views/MapView";

export default function NewApp() {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout } = controller;
    const [isLoaded, setIsLoaded] = useState(false);

    const { loginWithRedirect, isAuthenticated, user, isLoading: isAuthLoading } = useAuth0();

    userAPI.loadToken(useAuth0());
    const { data: selected_user, isLoading: isUserLoading } = useQuery({
        queryKey: ['getUser', user?.email],
        enabled: !!user?.email,
        queryFn: async () => {
            return await userAPI.getUser();
        }
    });

    const activeVisualMode = useMemo(() =>
        getActiveMode(selected_user?.user?.mode),
        [selected_user?.user?.mode]);

    useEffect(() => {
        if (!map.current && mapContainer.current && selected_user) {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: activeVisualMode === "dark" ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/harrymontano/cmn5irh6q001o01snbw5yembt?optimize=true',
                projection: 'globe',
                center: [151, -33],
                interactive: false,
                antialias: true
            });

            map.current.on('load', () => {
                map.current.originalCenter = map.current.getCenter();
                if (activeVisualMode === "dark") {
                    map.current.setFog({
                        color: 'rgba(255,255,255,0.06)',
                        'high-color': 'rgba(170,190,255,0.25)',
                        'space-color': '#05070f',
                        'horizon-blend': 0.18,
                        'star-intensity': 0.25
                    });
                }
                else {
                    map.current.setFog({
                        'color': '#ffffff',        // Keep the horizon line crisp white
                        'high-color': '#444444',   // REDUCED GLOW: Switched from #c0c0c0 to a darker grey
                        'space-color': '#242424',  // Darker grey space
                        'horizon-blend': 0.02,     // TIGHTENED: Lower value = sharper edge, less glow bleed
                        'star-intensity': 0.5
                    });

                }
                setIsLoaded(1);
            });
        }
    }, [selected_user]);

    useEffect(() => {
        if (map.current && isLoaded) {
            const newStyle = activeVisualMode === "dark" ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/harrymontano/cmn5irh6q001o01snbw5yembt?optimize=true';
            map.current.setStyle(newStyle);
            map.current.once('style.load', () => {
                if (activeVisualMode === "dark") {
                    map.current.setFog({
                        color: 'rgba(255,255,255,0.06)',
                        'high-color': 'rgba(170,190,255,0.25)',
                        'space-color': '#05070f',
                        'horizon-blend': 0.18,
                        'star-intensity': 0.25
                    });
                }
                else {
                    map.current.setFog({
                        'color': '#ffffff',        // Keep the horizon line crisp white
                        'high-color': '#444444',   // REDUCED GLOW: Switched from #c0c0c0 to a darker grey
                        'space-color': '#242424',  // Darker grey space
                        'horizon-blend': 0.02,     // TIGHTENED: Lower value = sharper edge, less glow bleed
                        'star-intensity': 0.5
                    });
                }

                setMapIcons(map, false);
            });
        }
    }, [activeVisualMode]);

    useEffect(() => {
        function handleDeviceLayout() {
            setDeviceLayout(dispatch, window.innerWidth < 1200 ? "mobile" : "desktop");
        }

        window.addEventListener("resize", handleDeviceLayout);
        handleDeviceLayout();

        return () => window.removeEventListener("resize", handleDeviceLayout);
    }, [dispatch]);

    const processedRoutesConfig = useMemo(() => {
        if (!selected_user) return routes;

        const orgs = selected_user.user.organisations || [];
        const hasClearways = orgs.includes('clearways');
        const isMultiOrgWithClearways = hasClearways && orgs.length > 1;

        return routes.map((route) => {
            if (route.key.includes("overview")) {
                // CASE: User has Clearways AND other orgs -> Keep Dropdown
                if (isMultiOrgWithClearways) {
                    return {
                        ...route,
                        collapse: [
                            {
                                name: "Stopmonitor Map",
                                key: "stopmonitor-view",
                                route: "/overview/stopmonitor",
                                component: MapView,
                                in_menu: true
                            },
                            {
                                name: "Clearways Map",
                                key: "clearways-view",
                                route: "/overview",
                                component: MapView,
                                in_menu: true
                            }
                        ],
                    };
                }

                // CASE: Single Org (Clearways only) or Standard (No Clearways)
                // We REMOVE the collapse property entirely. 
                // This forces BaseNavbarLink to render a simple <Link>
                const { collapse, ...parentOnly } = route;
                return {
                    ...parentOnly,
                    component: MapView,
                    route: "/overview", // Ensure route is correct
                    in_menu: true
                };
            }
            return route;
        });
    }, [routes, selected_user]);

    const allRoutes = useMemo(() => {
        if (!selected_user) return [];

        // Use processedRoutesConfig here instead of routes
        // console.log(processedRoutesConfig)
        return processedRoutesConfig.flatMap(route => {
            const { collapse, ...parent } = route;
            if (!collapse) return parent;
            return [
                parent,
                ...collapse.map(child => ({ ...child, parentKey: parent.key })),
            ];
        });
    }, [processedRoutesConfig, selected_user]);

    const renderRoutes = useMemo(() => {
        if (!selected_user) {
            return [];
        }

        // console.log(allRoutes)
        return allRoutes.map(function (route, key) {
            return <Route exact path={route.route} key={key} element={<route.component map={map} user={selected_user} />} />;
        });
    }, [routes, selected_user, allRoutes]);

    if (isAuthLoading || isUserLoading) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                        background: linearGradient(gradients.dark.main, gradients.dark.state),
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        top: '0px',
                        left: '0px',
                        'z-index': 0,
                        overflow: 'hidden'
                    })}
                >
                    <SoftBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="100%"
                    >
                        <CircularProgress color="light" />
                        <SoftTypography variant="button" fontWeight="medium" mt={2} color="light">
                            Loading...
                        </SoftTypography>
                    </SoftBox>
                </Box>
            </ThemeProvider>
        )
    }

    if (!isAuthenticated) {
        loginWithRedirect();
        return null;
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <Box component="div" ref={mapContainer}
                    sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                        background: linearGradient(gradients.dark.main, gradients.dark.state),
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        top: '0px',
                        left: '0px',
                        'z-index': 0,
                        overflow: 'hidden'
                    })}
                />
                <PageLayout>
                    <BaseNavbar
                        routes={processedRoutesConfig}
                        user={selected_user}
                        dark
                        action={{
                            type: "external",
                            label: "Settings",
                        }}
                    />
                    <Routes>
                        {isLoaded && renderRoutes}
                    </Routes>
                </PageLayout>
            </ThemeProvider>
        </BrowserRouter>
    );
}